import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, Renderer2, forwardRef } from '@angular/core';
import { SvcToastService } from '@solvace/component/svc-toast';
import { UserAvatarSize } from '@solvace/component/svc-user-avatar';
import { SvcCommentsFeedComponent } from '../svc-comments-feed/svc-comments-feed.component';
import { FeedItem } from '../../models/feed-item.model';
import { HttpErrorService, SvcMediaQuery } from 'projects/lib-shared-common/src/public-api';
import { environment } from 'projects/environments/environment';
import { JsonCallbackService } from '../../../../integrations/json-callback.service';
import { SvcFeedService } from '../../services/svc-feed.service';
import { catchError, finalize, of, tap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'svc-comments-list',
  templateUrl: './svc-comments-list.component.html',
  styleUrls: ['./svc-comments-list.component.scss'],
})
export class SvcCommentsListComponent implements OnInit, OnDestroy {
  @Output() onFeedClick = new EventEmitter<FeedItem>();

  @Input() feeds: FeedItem[] = [];

  protected UserAvatarSize = UserAvatarSize;
  protected someFeedIsLoading = false;
  private lastCursorPosition: { x: number; y: number };
  private currentFeedMenuOpened: { feed: FeedItem; element: HTMLElement; mousemoveCancel: () => void };

  constructor(
    @Inject(forwardRef(() => SvcCommentsFeedComponent))
    public parent: SvcCommentsFeedComponent,
    private renderer: Renderer2,
    private _jsonCallbackService: JsonCallbackService,
    private _svcFeedService: SvcFeedService,
    private _svcToastService: SvcToastService,
    private _httpErrorService: HttpErrorService,
    private _translocoService: TranslocoService,
    private _svcMediaQuery: SvcMediaQuery
  ) {}

  public ngOnInit(): void {}

  protected loadFeed(feed: FeedItem) {
    this.onFeedClick.emit(feed);
  }

  protected feedTogglePin(feed: FeedItem) {
    this.someFeedIsLoading = true;
    this._svcFeedService
      .changeFeedPin(feed.commentHeaderId, { value: !(feed.isPinned ?? false) })
      .pipe(
        tap((success) => {
          if (success) {
            this.parent?.refresh();
          } else {
            this._svcToastService.error(this._translocoService.translate('Occoreu um erro ao fixar/desafixar este registro'));
          }
        }),
        catchError((error) => {
          this._httpErrorService.showErrorInToast(error);
          return error;
        }),
        finalize(() => (this.someFeedIsLoading = false))
      )
      .subscribe();
  }

  protected feedMarkAsUnread(feed: FeedItem) {
    this.someFeedIsLoading = true;
    this._svcFeedService
      .markFeedAsUnread(feed.commentHeaderId)
      .pipe(
        tap((success) => {
          if (success) {
            feed.isMarkedUnRead = true;
            this.parent.addOneMoreInTotal();
          } else {
            this._svcToastService.error(this._translocoService.translate('Occoreu um erro ao marcar este registro como não lido'));
          }
        }),
        catchError((error) => {
          this._httpErrorService.showErrorInToast(error);
          return error;
        }),
        finalize(() => (this.someFeedIsLoading = false))
      )
      .subscribe();
  }

  public openFeedModule(feed: FeedItem, event?: MouseEvent) {
    if (!feed.jsonCallback) return;
    event?.stopImmediatePropagation();
    this.someFeedIsLoading = true;
    this._svcFeedService
      .getUserHasPermission({
        commentHeaderId: feed.commentHeaderId,
        registryId: feed.registryId || feed.registryUid,
      })
      .pipe(
        tap((hasPermission) => {
          if (hasPermission) {
            if (environment.isLocalhost) return;
            const result = this._jsonCallbackService.open(feed.jsonCallback, {
              record: { registryId: feed.registryInternalId, applicationId: feed.applicationId },
              openOnFeedTab: true,
            });
            if (result === 'modal') this.parent.closeSidebarIfOpened();
          } else {
            const message = this._translocoService.translate('Você não possui permissão acessar este registro');
            this._svcToastService.error(message);
          }
        }),
        catchError((error) => {
          const isRecordDeleted = error?.status === 404 || error?.status === 410;
          if (isRecordDeleted) {
            const message = this._translocoService.translate(
              'Este chat não está mais disponível, pois o registro foi removido.'
            );
            this._svcToastService.error(message);
          } else {
            this._httpErrorService.showErrorInToast(error);
          }
          return of(null);
        }),
        finalize(() => (this.someFeedIsLoading = false))
      )
      .subscribe();
  }

  protected onFeedMenuOpened(feed: FeedItem, element: HTMLElement) {
    this.currentFeedMenuOpened = {
      feed,
      element,
      mousemoveCancel: this.renderer.listen('window', 'mousemove', (event: MouseEvent) => {
        this.lastCursorPosition = { x: event.pageX, y: event.pageY };
      }),
    };
  }

  protected onFeedMenuClosed() {
    if (this.currentFeedMenuOpened) {
      const feed = this.currentFeedMenuOpened.feed as any;
      this.currentFeedMenuOpened.mousemoveCancel();
      if (feed.showOptions) {
        const element = this.currentFeedMenuOpened.element;
        const lastPosition = this.lastCursorPosition;
        feed.showOptions = this._svcMediaQuery.currentSize.isAboveSM && lastPosition && element.contains(document.elementFromPoint(lastPosition.x, lastPosition.y));
      }
    }
    this.currentFeedMenuOpened = null;
  }

  public ngOnDestroy(): void {
    this.currentFeedMenuOpened?.mousemoveCancel?.();
  }
}
